import React, { ComponentProps, useCallback, useMemo } from 'react';
import { useHistory, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

import ym from 'react-yandex-metrika';

import { DiagnosticsResults } from '@edu-frontend/modules/src/features/diagnostics/ui/DiagnosticsResults';
import { ThemeProvider } from '@edu-frontend/modules/src/shared/lib/ThemeProvider';
import { hrLight } from '@edu-frontend/modules/src/components/ui/Theme';

import { useDiagnosticsResults } from 'store/teacher/queries';

import * as lessonSelectors from '../../../../../common.components/lesson/selectors/common';

import './lesson-view__diagnostics-result.scss';

export function SpecCourseLessonViewDiagnosticsResults() {
    const teamId = +useParams<Record<string, string>>().classId;
    const info = useSelector(lessonSelectors.getLessonDiagnosticsInfo);
    const history = useHistory();
    const intl = useIntl();

    const slug = info && (typeof info === 'string' ? info : info.slug);
    const consultationUrl = typeof info === 'object' ? info.consultation : undefined;
    const consultationYm = (typeof info === 'object' && info.ym) || 'diagnostics_consultation';
    const diagnostics = useDiagnosticsResults(teamId, slug);
    const isNewTeacher = diagnostics.data?.is_new_teacher;

    const onConsultationClick = useCallback(() => {
        ym('reachGoal', consultationYm, {
            team_id: teamId,
            slug,
            type: 'click',
            is_new_teacher: isNewTeacher,
            place: 'banner',
        });
    }, [teamId, slug, isNewTeacher, consultationYm]);

    const onConsultationContextMenu = useCallback(() => {
        ym('reachGoal', consultationYm, {
            team_id: teamId,
            slug,
            type: 'contextmenu',
            is_new_teacher: isNewTeacher,
            place: 'banner',
        });
    }, [teamId, slug, isNewTeacher, consultationYm]);

    const onConsultationAuxClick = useCallback(() => {
        ym('reachGoal', consultationYm, {
            team_id: teamId,
            slug,
            type: 'auxclick',
            is_new_teacher: isNewTeacher,
            place: 'banner',
        });
    }, [teamId, slug, isNewTeacher, consultationYm]);

    const onBannerDownloadClick = useCallback(() => {
        ym('reachGoal', 'download_diagnostics_result', {
            team_id: teamId,
            slug,
            type: 'click',
            is_new_teacher: isNewTeacher,
            place: 'banner',
        });
    }, [teamId, slug, isNewTeacher]);

    const onBannerDownloadContextMenu = useCallback(() => {
        ym('reachGoal', 'download_diagnostics_result', {
            team_id: teamId,
            slug,
            type: 'contextmenu',
            is_new_teacher: isNewTeacher,
            place: 'banner',
        });
    }, [teamId, slug, isNewTeacher]);

    const onBannerDownloadAuxClick = useCallback(() => {
        ym('reachGoal', 'download_diagnostics_result', {
            team_id: teamId,
            slug,
            type: 'auxclick',
            is_new_teacher: isNewTeacher,
            place: 'banner',
        });
    }, [teamId, slug, isNewTeacher]);

    const onDownloadClick = useCallback(() => {
        ym('reachGoal', 'download_diagnostics_result', {
            team_id: teamId,
            slug,
            type: 'click',
            is_new_teacher: isNewTeacher,
            place: 'normal',
        });
    }, [teamId, slug, isNewTeacher]);

    const onDownloadContextMenu = useCallback(() => {
        ym('reachGoal', 'download_diagnostics_result', {
            team_id: teamId,
            slug,
            type: 'contextmenu',
            is_new_teacher: isNewTeacher,
            place: 'normal',
        });
    }, [teamId, slug, isNewTeacher]);

    const onDownloadAuxClick = useCallback(() => {
        ym('reachGoal', 'download_diagnostics_result', {
            team_id: teamId,
            slug,
            type: 'auxclick',
            is_new_teacher: isNewTeacher,
            place: 'normal',
        });
    }, [teamId, slug, isNewTeacher]);

    type ConsultationConfig = ComponentProps<typeof DiagnosticsResults>['consultation'];

    const consultation: ConsultationConfig = useMemo(
        () =>
            consultationUrl
                ? {
                      text: intl.formatMessage({ id: 'diagnostics.consultationHeader' }),
                      background: intl.formatMessage({ id: 'diagnostics.consultationBackground' }),
                      forecolor: intl.formatMessage({ id: 'diagnostics.consultationForecolor' }),
                      image: intl.formatMessage({ id: 'diagnostics.consultationImage' }),
                      buttonText: intl.formatMessage({ id: 'diagnostics.consultationButtonText' }),
                      buttonView: intl
                          .formatMessage({ id: 'diagnostics.consultationButtonView', defaultMessage: ' ' })
                          .trim() as NonNullable<ConsultationConfig>['buttonView'],
                      href: consultationUrl,
                      hrefTarget: consultationUrl.match(/^(https?:)?\/\//)
                          ? '_blank'
                          : (url): undefined => {
                                history.push(url);
                            },
                  }
                : undefined,
        [intl, consultationUrl, history]
    );

    return (
        <ThemeProvider isRoot={false} theme={hrLight}>
            <DiagnosticsResults
                className="lesson-view__diagnostics-result"
                consultation={consultation}
                consultationChapterDescription={intl.formatMessage({ id: 'diagnostics.info.description' })}
                consultationChapterHeader={intl.formatMessage({ id: 'diagnostics.info.header' })}
                data={diagnostics.isSuccess ? diagnostics.data || null : undefined}
                onBannerDownloadAuxClick={onBannerDownloadAuxClick}
                onBannerDownloadClick={onBannerDownloadClick}
                onBannerDownloadContextMenu={onBannerDownloadContextMenu}
                onConsultationAuxClick={onConsultationAuxClick}
                onConsultationClick={onConsultationClick}
                onConsultationContextMenu={onConsultationContextMenu}
                onDownloadAuxClick={onDownloadAuxClick}
                onDownloadClick={onDownloadClick}
                onDownloadContextMenu={onDownloadContextMenu}
            />
        </ThemeProvider>
    );
}
